// utils/whatsappHelper/orderLookup.ts - utils/whatsappHelper.ts split (sub-module orderLookup)

import { supabase } from '../../supabase/client';

import type {WhatsAppNumber} from './types.js';
import { pickBrandNumber } from './orderLookup/brandNumberPick.js';
import { toTypedNumber } from './orderLookup/toTypedNumber.js';

// Fallback: bot tanpa waNumber → nomor brand (RPC anon-safe get_brand_wa_numbers).
// Return null bila tak ada (jangan kirim ke nomor salah).
async function brandNumberFallback(brandId: string, csId?: string): Promise<string | null> {
  try {
    const { data } = await supabase.rpc('get_brand_wa_numbers', { p_brand_id: brandId });
    const phone = pickBrandNumber(data as any, csId);
    if (phone) console.log('[WhatsApp Helper] Fallback nomor brand:', phone);
    return phone;
  } catch { return null; }
}

/**
 * Fetch active WhatsApp number — full otomatis, tanpa hardcode.
 * Priority (semua brand-scoped): 0) bot eksplisit form, 1) brand+CS, 2) brand,
 * 3) fallback nomor brand (RPC get_brand_wa_numbers) — supaya bot waNumber NULL
 * tetap dapat nomor brandnya sendiri.
 * Match CS-saja lintas brand DILARANG (insiden 2026-10-27: konfirmasi brand A
 * mendarat di nomor brand B) — hanya dipakai bila brandId tidak diberikan
 * (konteks admin tanpa brand). Tanpa kecocokan → null (jangan kirim salah).
 * Source: wa_bot_configs (single source of truth, replaced whatsapp_numbers)
 */
export async function getWhatsAppNumberForOrder(
  brandId?: string,
  csId?: string,
  pipelineType?: string,
  preferredBotId?: string | null
): Promise<WhatsAppNumber | null> {
  try {
    const typedNumbers: WhatsAppNumber[] = [];
    try {
      let query = supabase.from('wa_bot_configs').select('*').eq('isActive', true);
    // 'physical_form' pada pemanggil = "nomor pengirim fisik" (konfirmasi order, follow-up).
    // WAJIB ikut 'physical_both' (bot chat+order: Yudaivu/Gerd Hero/Pusat COD) — dulu filter
    // ketat physical_form membuat brand ber-pipeline physical_both TIDAK punya nomor sama
    // sekali → tombol konfirmasi WA di halaman thanks hilang/nyasar inbox
    // (insiden 2026-10-16: konfirmasi Pusat COD mendarat di inbox Yudaivu).
    if (pipelineType === 'physical_form') query = query.in('pipeline_type', ['physical_form', 'physical_both']);
    else if (pipelineType) query = query.eq('pipeline_type', pipelineType);
    const { data: configs, error } = await query;

    if (error) {
      console.error('[WhatsApp Helper] Error fetching configs:', error);
    } else if (!configs || configs.length === 0) {
      console.warn('[WhatsApp Helper] No active bot configs found');
    } else {
      // Hanya bot yang punya nomor — bot waNumber NULL tak boleh "menang" lalu kirim kosong.
      typedNumbers.push(...configs.map(toTypedNumber).filter(n => n.phoneNumber));

      const found =
        (preferredBotId ? typedNumbers.find(n => n.id === preferredBotId) : null)
        || (brandId && csId ? typedNumbers.find(n => n.assignedBrandIds?.includes(brandId) && n.assignedCsIds?.includes(csId)) : null)
        || (brandId ? typedNumbers.find(n => n.assignedBrandIds?.includes(brandId)) : null);
      if (found) return found;

      if (brandId) {
        // Brand jelas tapi tak ada bot bernomor → JANGAN tebak nomor brand lain.
        console.warn('[WhatsApp Helper] No bot number for brand, trying brand fallback:', brandId);
      } else if (csId) {
        // Tanpa brandId (konteks admin): match CS-saja masih boleh.
        const byCs = typedNumbers.find(n => n.assignedCsIds?.includes(csId));
        if (byCs) return byCs;
      }
    }
    } catch (qErr) {
      console.error('[WhatsApp Helper] Bot query failed, trying brand fallback:', qErr);
    }

    // Prioritas 3: fallback nomor brand (wa_bot_configs tanpa waNumber).
    if (brandId) {
      const phone = await brandNumberFallback(brandId, csId);
      if (phone) return { ...toTypedNumber({ id: 'brand-fallback', botName: 'brand-fallback', waNumber: phone, brandId }), id: 'brand-fallback' };
      return null;
    }

    // Tidak ada yang cocok → JANGAN kirim (cegah salah brand).
    console.warn('[WhatsApp Helper] No matching WhatsApp number found for brand:', brandId, 'CS:', csId);
    return null;
  } catch (error) {
    console.error('[WhatsApp Helper] Unexpected error:', error);
    return null;
  }
}
